import { Link, useLocation } from "wouter";
import { BrandMark } from "./BrandMark";
import {
  LayoutDashboard,
  Store,
  Users,
  ShoppingBag,
  Receipt,
  Tags,
  Ticket,
  Bell,
  Database,
  Settings as SettingsIcon,
  Sparkles,
  type LucideIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { appEnv, isProduction } from "@/lib/firebase";
import { Badge } from "@/components/ui/badge";

export interface NavItem {
  href: string;
  label: string;
  icon: LucideIcon;
}

const NAV_ITEMS: NavItem[] = [
  { href: "/", label: "Painel", icon: LayoutDashboard },
  { href: "/vendedores", label: "Vendedores", icon: Store },
  { href: "/clientes", label: "Clientes", icon: Users },
  { href: "/pedidos", label: "Pedidos", icon: ShoppingBag },
  { href: "/faturamento", label: "Faturamento", icon: Receipt },
  { href: "/tipos-de-produto", label: "Tipos de produto", icon: Tags },
  { href: "/cupons", label: "Cupons", icon: Ticket },
  { href: "/notificacoes", label: "Notificações", icon: Bell },
  { href: "/configuracoes", label: "Configurações", icon: SettingsIcon },
  { href: "/ia", label: "IA", icon: Sparkles },
  { href: "/dados", label: "Dados", icon: Database },
];

/** O item ativo é o de caminho mais específico que casa com a rota atual. */
function isActive(current: string, href: string): boolean {
  if (href === "/") return current === "/";
  return current === href || current.startsWith(`${href}/`);
}

export function SidebarNav({ onNavigate }: { onNavigate?: () => void }) {
  const [location] = useLocation();

  return (
    <nav className="flex flex-col gap-1" aria-label="Navegação principal">
      {NAV_ITEMS.map((item) => {
        const active = isActive(location, item.href);
        return (
          <Link
            key={item.href}
            href={item.href}
            onClick={onNavigate}
            aria-current={active ? "page" : undefined}
            className={cn(
              "flex min-h-10 items-center gap-3 rounded-md px-3 py-2 text-small font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
              active
                ? "bg-primary text-primary-foreground"
                : "text-muted-foreground hover:bg-muted hover:text-foreground",
            )}
          >
            <item.icon className="size-4 shrink-0" />
            <span className="truncate">{item.label}</span>
          </Link>
        );
      })}
    </nav>
  );
}

export function SidebarBrand() {
  return (
    <div className="flex items-center gap-2.5">
      <BrandMark className="size-9" />
      <div className="min-w-0">
        <p className="truncate text-medium font-black leading-tight">Zé Padoca</p>
        <p className="truncate text-caption text-muted-foreground">Gestão</p>
      </div>
    </div>
  );
}

/**
 * O selo de ambiente só aparece fora de produção. Ele existe porque o painel
 * escreve em config e em cadastro: confundir homologação com produção aqui
 * muda o que a base inteira de clientes enxerga.
 */
export function EnvironmentBadge() {
  if (isProduction) return null;
  return (
    <Badge variant="warning" className="w-full justify-center uppercase">
      {appEnv}
    </Badge>
  );
}
